import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
    FiUsers,
    FiUserPlus,
    FiFileText,
    FiAward,
    FiPlus,
    FiArrowRight,
    FiClock,
    FiShield,
    FiTruck,
    FiDollarSign,
} from "react-icons/fi";
import { useApp } from "../../../context/AppContext";
import api from "../../../services/api";
import reconciliationService from "../../../services/reconciliationService";
import CollegeLogoBadge from "../../../components/common/CollegeLogoBadge";

export default function ClerkDashboard() {
    const { user, collegeName } = useApp();
    const navigate = useNavigate();

    const [stats, setStats] = useState({
        totalStudents: 0,
        pendingAdmissions: 0,
        bonafideIssued: 0,
        leavingIssued: 0,
    });
    const [recentBonafide, setRecentBonafide] = useState([]);
    const [recentLeaving, setRecentLeaving] = useState([]);
    const [cashSummary, setCashSummary] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDashboardData = async () => {
            setLoading(true);
            try {
                const [studentsRes, admissionsRes, bonafideRes, leavingRes, cashRes] = await Promise.allSettled([
                    api.get("/students", { params: { limit: 1 } }),
                    api.get("/admissions", { params: { status: "Under Review", limit: 1 } }),
                    api.get("/bonafide", { params: { limit: 5 } }),
                    api.get("/leaving", { params: { limit: 5 } }),
                    reconciliationService.getClerkTodaySummary(),
                ]);

                if (cashRes.status === "fulfilled" && cashRes.value.data) {
                    setCashSummary(cashRes.value.data);
                }

                const totalStudents =
                    studentsRes.status === "fulfilled"
                        ? studentsRes.value.pagination?.total || studentsRes.value.data?.total || 0
                        : 0;

                const pendingAdmissions =
                    admissionsRes.status === "fulfilled"
                        ? admissionsRes.value.pagination?.total || admissionsRes.value.data?.total || admissionsRes.value.data?.length || 0
                        : 0;

                const bonafideDocs =
                    bonafideRes.status === "fulfilled"
                        ? Array.isArray(bonafideRes.value.data)
                            ? bonafideRes.value.data
                            : bonafideRes.value.data?.data || []
                        : [];

                const leavingDocs =
                    leavingRes.status === "fulfilled"
                        ? Array.isArray(leavingRes.value.data)
                            ? leavingRes.value.data
                            : leavingRes.value.data?.data || []
                        : [];

                const totalBonafide =
                    bonafideRes.status === "fulfilled"
                        ? bonafideRes.value.pagination?.total || bonafideDocs.length
                        : 0;

                const totalLeaving =
                    leavingRes.status === "fulfilled"
                        ? leavingRes.value.pagination?.total || leavingDocs.length
                        : 0;

                setStats({
                    totalStudents,
                    pendingAdmissions,
                    bonafideIssued: totalBonafide,
                    leavingIssued: totalLeaving,
                });
                setRecentBonafide(bonafideDocs.slice(0, 5));
                setRecentLeaving(leavingDocs.slice(0, 5));
            } catch (err) {
                toast.error(err.message || "Failed to load clerk dashboard data");
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    if (loading) {
        return (
            <div className="loading-center">
                <div className="spinner" />
                Loading clerk dashboard…
            </div>
        );
    }

    return (
        <div>
            {/* ── Page Header ── */}
            <div className="page-header" style={{ flexWrap: "wrap", gap: "1rem", marginBottom: "1.25rem", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.85rem" }}>
                    <CollegeLogoBadge size={52} />
                    <div>
                        <div style={{ fontSize: "11px", fontWeight: 700, color: "#003b67", letterSpacing: "0.04em", textTransform: "uppercase" }}>
                            {collegeName}
                        </div>
                        <h1 className="page-title" style={{ margin: "2px 0 0 0" }}>Welcome back, {user?.displayName || user?.username} 🗂️</h1>
                        <p className="page-subtitle" style={{ margin: "2px 0 0 0" }}>Administrative Desk &amp; Student Records Management</p>
                    </div>
                </div>
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                    <button
                        className="btn btn-outline"
                        onClick={() => navigate("/clerk/fines")}
                        style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
                    >
                        <FiShield size={14} /> Fines
                    </button>
                    <button
                        className="btn btn-outline"
                        onClick={() => navigate("/clerk/transport")}
                        style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
                    >
                        <FiTruck size={14} /> Bus Pass
                    </button>
                    <button
                        className="btn btn-outline"
                        onClick={() => navigate("/clerk/fees/new")}
                        style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
                    >
                        <FiPlus size={14} /> Collect Fee
                    </button>
                    <button
                        className="btn btn-outline"
                        onClick={() => navigate("/clerk/bonafide/new")}
                        style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
                    >
                        <FiPlus size={14} /> New Bonafide
                    </button>
                    <button
                        className="btn btn-outline"
                        onClick={() => navigate("/clerk/leaving/new")}
                        style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
                    >
                        <FiPlus size={14} /> New Leaving Cert
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={() => navigate("/clerk/students/add")}
                        style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
                    >
                        <FiUserPlus size={14} /> Add Student
                    </button>
                </div>
            </div>

            {/* ── KPI Metric Cards ── */}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
                    gap: "1rem",
                    marginBottom: "1.5rem",
                }}
            >
                <div
                    className="card"
                    style={{ padding: "1.1rem", cursor: "pointer" }}
                    onClick={() => navigate("/clerk/students")}
                >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                        <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", letterSpacing: "0.05em" }}>
                            Total Students
                        </span>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: "#e0f2fe", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <FiUsers size={15} color="#0284c7" />
                        </div>
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 900, color: "var(--text)" }}>
                        {stats.totalStudents.toLocaleString("en-IN")}
                    </div>
                    <div style={{ fontSize: 11.5, color: "#0284c7", marginTop: 4, display: "flex", alignItems: "center", gap: 3 }}>
                        View All Students <FiArrowRight size={10} />
                    </div>
                </div>

                <div
                    className="card"
                    style={{ padding: "1.1rem", cursor: "pointer" }}
                    onClick={() => navigate("/clerk/admissions")}
                >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                        <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", letterSpacing: "0.05em" }}>
                            Pending Admissions
                        </span>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <FiClock size={15} color="#d97706" />
                        </div>
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 900, color: stats.pendingAdmissions > 0 ? "#d97706" : "var(--text)" }}>
                        {stats.pendingAdmissions.toLocaleString("en-IN")}
                    </div>
                    <div style={{ fontSize: 11.5, color: "#d97706", marginTop: 4, display: "flex", alignItems: "center", gap: 3 }}>
                        Admission Board <FiArrowRight size={10} />
                    </div>
                </div>

                <div
                    className="card"
                    style={{ padding: "1.1rem", cursor: "pointer" }}
                    onClick={() => navigate("/clerk/bonafide")}
                >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                        <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", letterSpacing: "0.05em" }}>
                            Bonafide Issued
                        </span>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <FiAward size={15} color="#7c3aed" />
                        </div>
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 900, color: "var(--text)" }}>
                        {stats.bonafideIssued.toLocaleString("en-IN")}
                    </div>
                    <div style={{ fontSize: 11.5, color: "#7c3aed", marginTop: 4, display: "flex", alignItems: "center", gap: 3 }}>
                        Certificate Records <FiArrowRight size={10} />
                    </div>
                </div>

                <div
                    className="card"
                    style={{ padding: "1.1rem", cursor: "pointer" }}
                    onClick={() => navigate("/clerk/leaving")}
                >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                        <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", letterSpacing: "0.05em" }}>
                            Leaving Certificates
                        </span>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <FiFileText size={15} color="#4b5563" />
                        </div>
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 900, color: "var(--text)" }}>
                        {stats.leavingIssued.toLocaleString("en-IN")}
                    </div>
                    <div style={{ fontSize: 11.5, color: "#4b5563", marginTop: 4, display: "flex", alignItems: "center", gap: 3 }}>
                        Leaving Records <FiArrowRight size={10} />
                    </div>
                </div>
            </div>

            {/* ── Cash Collection & Handover Quick Desk ── */}
            <div
                className="card"
                style={{
                    padding: "1.25rem",
                    marginBottom: "1.5rem",
                    background: "linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%)",
                    border: "1px solid #bbf7d0",
                }}
            >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "1rem" }}>
                    <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700, color: "#166534", fontSize: 14 }}>
                            <FiDollarSign size={16} /> Daily Cash Collection &amp; Handover Desk
                        </div>
                        <p style={{ margin: "4px 0 0", fontSize: 12.5, color: "var(--text-muted)" }}>
                            You currently have <strong>₹{Number(cashSummary?.expectedAmount || 0).toLocaleString("en-IN")}</strong> in physical cash across <strong>{cashSummary?.receiptCount || 0}</strong> receipt(s) awaiting handover to the Accounts Department.
                        </p>
                    </div>
                    <button
                        className="btn btn-primary"
                        onClick={() => navigate("/clerk/cash-handover")}
                        style={{ display: "flex", alignItems: "center", gap: 6, padding: "0.55rem 1rem", fontSize: 13 }}
                    >
                        Go to Cash Handover Desk <FiArrowRight size={13} />
                    </button>
                </div>
            </div>

            {/* ── Content Grid: Recent Certificates ── */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "1.25rem" }}>
                {/* Recent Bonafide Certificates */}
                <div className="card">
                    <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontWeight: 700 }}>
                            <FiAward size={14} color="#7c3aed" /> Recent Bonafide Certificates
                        </span>
                        <button
                            className="btn btn-outline btn-sm"
                            onClick={() => navigate("/clerk/bonafide")}
                            style={{ display: "flex", alignItems: "center", gap: "0.3rem", fontSize: 12 }}
                        >
                            View All <FiArrowRight size={11} />
                        </button>
                    </div>
                    <div className="card-body" style={{ padding: 0 }}>
                        {recentBonafide.length === 0 ? (
                            <div style={{ padding: "1.5rem", textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
                                No bonafide certificates issued recently.
                            </div>
                        ) : (
                            recentBonafide.map((item, idx) => (
                                <div
                                    key={item._id || idx}
                                    style={{
                                        padding: "0.75rem 1rem",
                                        borderBottom: "1px solid var(--border)",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        fontSize: 13,
                                    }}
                                >
                                    <div>
                                        <div style={{ fontWeight: 600 }}>{item.student?.fullName || item.fullName || "Student"}</div>
                                        <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                                            Sr No: {item.srNo || "—"} · Date: {item.issueDate?.slice(0, 10) || "—"}
                                        </div>
                                    </div>
                                    <button
                                        className="btn btn-outline btn-sm"
                                        onClick={() => navigate(`/clerk/bonafide/${item._id}/print`)}
                                        style={{ fontSize: 11, padding: "2px 8px" }}
                                    >
                                        Print
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Recent Leaving Certificates */}
                <div className="card">
                    <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontWeight: 700 }}>
                            <FiFileText size={14} color="#4b5563" /> Recent Leaving Certificates
                        </span>
                        <button
                            className="btn btn-outline btn-sm"
                            onClick={() => navigate("/clerk/leaving")}
                            style={{ display: "flex", alignItems: "center", gap: "0.3rem", fontSize: 12 }}
                        >
                            View All <FiArrowRight size={11} />
                        </button>
                    </div>
                    <div className="card-body" style={{ padding: 0 }}>
                        {recentLeaving.length === 0 ? (
                            <div style={{ padding: "1.5rem", textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
                                No leaving certificates issued recently.
                            </div>
                        ) : (
                            recentLeaving.map((item, idx) => (
                                <div
                                    key={item._id || idx}
                                    style={{
                                        padding: "0.75rem 1rem",
                                        borderBottom: "1px solid var(--border)",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        fontSize: 13,
                                    }}
                                >
                                    <div>
                                        <div style={{ fontWeight: 600 }}>{item.student?.fullName || item.fullName || "Student"}</div>
                                        <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                                            Serial No: {item.serialNo || "—"} · Date: {item.issueDate?.slice(0, 10) || "—"}
                                        </div>
                                    </div>
                                    <button
                                        className="btn btn-outline btn-sm"
                                        onClick={() => navigate(`/clerk/leaving/${item._id}/print`)}
                                        style={{ fontSize: 11, padding: "2px 8px" }}
                                    >
                                        Print
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}